import { clearDesk, getDeskItems, removeFromDesk, type DeskItem } from "../lib/desk.ts";
import { el, button, svgEl } from "../lib/dom.ts";
import { getText } from "../lib/sefaria.ts";
import { getSourceType } from "../lib/typeSystem.ts";
import { trapFocus } from "../lib/focusTrap.ts";
import { syncOverlayBackground } from "../lib/overlayBackground.ts";
import { openPeek } from "./peek.ts";
import { skeletonLines } from "./skeleton.ts";
import { textBlock, errorRow } from "./textBlock.ts";

export interface SourceDeskActions {
  onOpenItem?: (item: DeskItem) => boolean;
  onPreview?: (item: DeskItem) => void;
  onBookmark?: (item: DeskItem, btn?: HTMLButtonElement) => void;
  onBeforeDrawerOpen?: () => void;
  announce?: (msg: string) => void;
}

// Toast dwell time, and the dock-height fallbacks used only when getBoundingClientRect
// reports 0 (jsdom / before first layout) - keyed by expand + height mode.
const TOAST_MS = 6000;
const DOCK_H = { tall: 446, open: 292, collapsed: 56 };

let handlers: SourceDeskActions = {};
let dock: HTMLElement | null = null;
let expanded = false;
let heightMode: "default" | "tall" = "default";
let gridSetting = 2;
let lastCount = 0;
let keysWired = false;
let drawerScrim: HTMLElement | null = null;
let drawerSheet: HTMLElement | null = null;
let drawerBody: HTMLElement | null = null;
let drawerRelease: (() => void) | null = null;
let drawerOpener: HTMLElement | null = null;
let toast: HTMLElement | null = null;
let toastTimer: ReturnType<typeof setTimeout> | null = null;
let toastStarted = 0;
let toastRemaining = TOAST_MS;

function bookmarkIcon(): SVGSVGElement {
  const icon = svgEl("svg", {
    viewBox: "0 0 24 24",
    width: "16",
    height: "16",
    "aria-hidden": "true",
    focusable: "false",
  });
  icon.append(
    // the ONE bookmark ribbon shape - byte-identical to the header (.hi-bookmark) and
    // the card bookmark (.xcard-bookmark) so a saved source reads the same everywhere.
    svgEl("path", {
      d: "M6 2h12v20l-6-4-6 4z",
      fill: "none",
      stroke: "currentColor",
      "stroke-width": "2",
      "stroke-linejoin": "round",
    }),
  );
  return icon;
}

function bookmarkButton(item: DeskItem): HTMLButtonElement {
  const b = el("button", "desk-btn desk-btn-quiet desk-bookmark");
  b.type = "button";
  b.append(bookmarkIcon());
  b.setAttribute("aria-label", `Bookmark ${item.enTitle}`);
  b.title = "Bookmark";
  b.addEventListener("click", () => handlers.onBookmark?.(item, b));
  if (!handlers.onBookmark) b.hidden = true;
  return b;
}

function sourceType(item: DeskItem): ReturnType<typeof getSourceType> {
  return getSourceType(item.category);
}

function openItem(item: DeskItem): void {
  const opened = handlers.onOpenItem?.(item);
  if (opened === false || !handlers.onOpenItem) previewItem(item);
}

function previewItem(item: DeskItem): void {
  if (handlers.onPreview) {
    handlers.onPreview(item);
    return;
  }
  const type = sourceType(item);
  openPeek({
    ref: item.ref,
    label: item.enTitle,
    labelHe: item.heTitle,
    relationshipLabel: "from your desk",
    colorVar: type.colorVar,
    onOpenHere: handlers.onOpenItem ? () => handlers.onOpenItem?.(item) : undefined,
  });
}

export function buildDeskCard(item: DeskItem, opts: { compact?: boolean } = {}): HTMLElement {
  const type = sourceType(item);
  const card = el("article", `desk-card${opts.compact ? " desk-card-compact" : ""}`);
  card.dataset.ref = item.ref;
  card.style.setProperty("--tc", `var(${type.colorVar})`);

  const head = el("div", "desk-card-head");
  const dot = el("span", "ec-dot desk-dot");
  dot.style.setProperty("--dot", `var(${type.colorVar})`);
  dot.setAttribute("aria-hidden", "true");
  const titles = el("div", "desk-card-titles");
  if (item.heTitle) {
    const he = el("span", "desk-card-he", item.heTitle);
    he.lang = "he";
    he.dir = "rtl";
    titles.append(he);
  }
  titles.append(el("span", "desk-card-en", item.enTitle));
  head.append(dot, titles, el("span", "desk-card-type", type.label));

  const body = el("div", "desk-card-body");
  body.tabIndex = 0;
  body.setAttribute("role", "region");
  body.setAttribute("aria-label", `${item.enTitle} text on the Source Desk`);
  body.append(skeletonLines(2));

  void getText(item.ref)
    .then((t) => {
      if (!card.isConnected) return;
      body.replaceChildren(textBlock(t.he, t.en, "desk-stext"));
    })
    .catch((err) => {
      if (!card.isConnected) return;
      body.replaceChildren(errorRow(err));
    });

  const acts = el("div", "desk-card-actions");
  const open = button("desk-btn desk-btn-go", "Open", () => openItem(item));
  open.setAttribute("aria-label", `Open ${item.enTitle} in your journey`);
  const mark = bookmarkButton(item);
  const close = button("desk-btn desk-btn-quiet", "Close", () => removeFromDesk(item.ref));
  close.setAttribute("aria-label", `Close ${item.enTitle} on the Source Desk`);
  acts.append(open, mark, close);

  card.append(head, body, acts);
  return card;
}

function titleSummary(items: DeskItem[]): string {
  const names = items.map((item) => item.enTitle).filter(Boolean);
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}

function publishDeskHeight(): void {
  const root = document.documentElement;
  if (!dock || !dock.isConnected) {
    root.style.setProperty("--desk-dock-h", "0px");
    return;
  }
  const fallback = expanded ? (heightMode === "tall" ? DOCK_H.tall : DOCK_H.open) : DOCK_H.collapsed;
  const h = Math.ceil(dock.getBoundingClientRect().height || fallback);
  root.style.setProperty("--desk-dock-h", `${h}px`);
}

function wireKeys(): void {
  if (keysWired || typeof window === "undefined") return;
  keysWired = true;
  window.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || !expanded) return;
    expanded = false;
    renderDeskDock();
    e.stopPropagation();
  });
}

function buildCollapsed(items: DeskItem[]): HTMLElement {
  const bar = el("div", "desk-bar");
  bar.append(el("div", "desk-bar-title", `Source Desk - ${items.length} open`));
  bar.append(el("div", "desk-bar-summary", titleSummary(items)));
  const open = button("desk-bar-open", "Open", () => {
    expanded = true;
    renderDeskDock();
  });
  open.setAttribute("aria-label", "Open Source Desk");
  bar.append(open);
  return bar;
}

function buildExpanded(items: DeskItem[]): HTMLElement {
  const panel = el("div", `desk-panel desk-panel-${heightMode}`);
  const head = el("div", "desk-panel-head");
  const title = el("div", "desk-panel-title");
  title.append(el("div", "desk-panel-kicker", "Source Desk"), el("div", "desk-panel-sub", `${items.length} open`));
  const controls = el("div", "desk-panel-controls");
  controls.append(el("span", "desk-view-label", "View type"));
  for (const n of [1, 2, 3]) {
    if (n === 3 && items.length < 3) continue;
    const b = button("desk-col-btn", String(n), () => {
      gridSetting = n;
      renderDeskDock();
    });
    b.setAttribute("aria-pressed", String(gridSetting === n));
    b.setAttribute("aria-label", `${n} desk ${n === 1 ? "column" : "columns"}`);
    controls.append(b);
  }
  const tall = button("desk-col-btn", heightMode === "tall" ? "Short" : "Tall", () => {
    heightMode = heightMode === "tall" ? "default" : "tall";
    renderDeskDock();
  });
  tall.setAttribute("aria-label", "Toggle Source Desk height");
  const clear = button("desk-clear", "Clear Desk", clearDesk);
  const collapse = button("desk-collapse", "Done", () => {
    expanded = false;
    renderDeskDock();
  });
  controls.append(tall, clear, collapse);
  head.append(title, controls);

  const grid = el("div", "desk-grid");
  const cols = Math.max(1, Math.min(items.length, gridSetting));
  grid.style.setProperty("--desk-cols", String(cols));
  items.forEach((item) => grid.append(buildDeskCard(item)));
  panel.append(head, grid);
  return panel;
}

export function renderDeskDock(nextHandlers?: SourceDeskActions): void {
  if (nextHandlers) handlers = nextHandlers;
  wireKeys();
  const items = getDeskItems();
  if (items.length > lastCount) expanded = false;
  lastCount = items.length;
  dock?.remove();
  dock = null;
  if (!items.length) {
    publishDeskHeight();
    renderDeskDrawer();
    return;
  }
  dock = el("section", `desk-dock${expanded ? " desk-dock-expanded" : " desk-dock-collapsed"}`);
  dock.setAttribute("aria-label", "Source Desk");
  dock.append(expanded ? buildExpanded(items) : buildCollapsed(items));
  document.body.append(dock);
  publishDeskHeight();
  renderDeskDrawer();
}

function buildDeskDrawerBody(): HTMLElement {
  const panel = el("div", "desk-drawer-panel");
  const items = getDeskItems();
  panel.append(el("div", "desk-panel-kicker", "Source Desk"));
  if (!items.length) {
    panel.append(el("div", "desk-empty", "No sources on your desk yet."));
    return panel;
  }
  const stack = el("div", "desk-drawer-stack");
  items.forEach((item) => stack.append(buildDeskCard(item, { compact: true })));
  panel.append(stack);
  return panel;
}

function renderDeskDrawer(): void {
  if (!drawerBody) return;
  const hadBodyFocus = document.activeElement instanceof HTMLElement && drawerBody.contains(document.activeElement);
  drawerBody.replaceChildren(buildDeskDrawerBody());
  if (hadBodyFocus) {
    const next = drawerBody.querySelector<HTMLButtonElement>("button") ?? drawerSheet?.querySelector<HTMLButtonElement>(".drawer-close");
    next?.focus();
  }
}

export function refreshDeskDrawer(): void {
  renderDeskDrawer();
}

export function setDeskDockOpen(open: boolean): void {
  expanded = open;
  renderDeskDock();
}

export function openDeskDrawer(nextHandlers?: SourceDeskActions, verseHead?: HTMLElement | null): void {
  if (nextHandlers) handlers = nextHandlers;
  if (drawerSheet) {
    closeDeskDrawer();
    return;
  }
  handlers.onBeforeDrawerOpen?.();
  drawerOpener = document.activeElement as HTMLElement | null;
  const scrim = el("div", "drawer-scrim");
  const sheet = el("div", "journey-drawer desk-drawer");
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-modal", "true");
  sheet.setAttribute("aria-label", "Source Desk");
  const grip = el("div", "drawer-grip");
  grip.setAttribute("aria-hidden", "true");
  const close = button("drawer-close", "Done", closeDeskDrawer);
  close.setAttribute("aria-label", "Close Source Desk");
  const body = el("div", "drawer-body");
  body.append(buildDeskDrawerBody());
  sheet.append(grip, close);
  // The selected pasuk (built by the navigator) pins between Done and the
  // scrolling card list, so the sources scroll beneath while the verse stays in
  // view. The divider lifts (shadow) only once a card scrolls under it.
  if (verseHead) {
    sheet.append(verseHead);
    body.addEventListener(
      "scroll",
      () => sheet.classList.toggle("is-scrolled", body.scrollTop > 0),
      { passive: true },
    );
  }
  sheet.append(body);
  scrim.addEventListener("click", () => closeDeskDrawer());
  document.body.append(scrim, sheet);
  // The verse pane fades at its bottom only when it actually overflows its cap
  // (reading scrollHeight forces the needed reflow in a real browser; jsdom
  // reports 0, so this is a no-op there - the fade is enhancement-only).
  if (verseHead) {
    const vs = verseHead.querySelector<HTMLElement>(".desk-verse-scroll");
    if (vs) vs.classList.toggle("is-clipped", vs.scrollHeight > vs.clientHeight + 1);
  }
  drawerScrim = scrim;
  drawerSheet = sheet;
  drawerBody = body;
  syncOverlayBackground(); // inert the app behind the desk drawer
  const handle = trapFocus(sheet, { initialFocus: close, onEscape: closeDeskDrawer });
  drawerRelease = () => handle.release();
}

export function closeDeskDrawer(restoreFocus = true): void {
  drawerRelease?.();
  drawerRelease = null;
  drawerScrim?.remove();
  drawerSheet?.remove();
  drawerScrim = null;
  drawerSheet = null;
  drawerBody = null;
  syncOverlayBackground(); // un-inert the background BEFORE returning focus to it
  const opener = drawerOpener;
  drawerOpener = null;
  if (restoreFocus) opener?.focus?.();
}

function closeToast(): void {
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = null;
  toast?.remove();
  toast = null;
}

function armToast(ms: number): void {
  if (toastTimer) clearTimeout(toastTimer);
  toastStarted = Date.now();
  toastTimer = setTimeout(closeToast, ms);
}

export function showDeskToast(
  msg: string,
  action?: string,
  onAction?: () => void,
  announce?: (m: string) => void,
): void {
  closeToast();
  const text = action ? `${msg} ${action}` : msg;
  if (announce) {
    announce(text);
  } else {
    const live = document.querySelector(".sr-status");
    if (live) live.textContent = text;
  }

  const chip = el("div", "desk-toast");
  chip.setAttribute("role", "status");
  chip.append(el("span", "desk-toast-msg", msg));
  if (action && onAction) {
    const b = button("desk-toast-action", action, () => {
      closeToast();
      onAction();
    });
    chip.append(b);
  }
  chip.addEventListener("mouseenter", () => {
    if (!toastTimer) return;
    clearTimeout(toastTimer);
    toastTimer = null;
    toastRemaining = Math.max(1000, toastRemaining - (Date.now() - toastStarted));
  });
  chip.addEventListener("mouseleave", () => armToast(toastRemaining));
  chip.addEventListener("focusin", () => {
    if (!toastTimer) return;
    clearTimeout(toastTimer);
    toastTimer = null;
    toastRemaining = Math.max(1000, toastRemaining - (Date.now() - toastStarted));
  });
  chip.addEventListener("focusout", () => armToast(toastRemaining));
  document.body.append(chip);
  toast = chip;
  toastRemaining = TOAST_MS;
  armToast(toastRemaining);
}

export function _resetSourceDeskForTests(): void {
  dock?.remove();
  closeDeskDrawer();
  closeToast();
  dock = null;
  expanded = false;
  heightMode = "default";
  gridSetting = 2;
  lastCount = 0;
  handlers = {};
  document.documentElement.style.setProperty("--desk-dock-h", "0px");
}
